import React from 'react';
import { createPortal } from 'react-dom';
import {
  formatPresentationLayoutBytes,
  type ProgramPresentationListItem,
} from '../../utils/programPresentationList';
import {
  presentationRefSlideLinkStateLabel,
  resolvePresentationRefSlideLinkState,
  sumSlidePageCountForOwners,
} from '../../utils/programPresentationLinkState';
import ModalBodyLoadingSpinner from '../common/ModalBodyLoadingSpinner';
import { programContentSlidePresenceKey } from '../../services/programContentSlideService';
import { readProgramPresentationListStatsCache } from '../../utils/programPresentationListStatsCache';
import { usePresentationOwnerStatsQuery } from '../../query/hooks/usePresentationOwnerStatsQuery';
import ProgramPresentationListRowActions from './ProgramPresentationListRowActions';

const SYNC_MIRROR_ROW_TITLE =
  '동기화 미러 — 대표 시간표(원본) 행에서만 열고 편집할 수 있습니다.';

function presentationListOpenCellProps(
  row: ProgramPresentationListItem,
  onOpen: () => void,
  extraClass = ''
): React.TdHTMLAttributes<HTMLTableCellElement> {
  if (row.isSyncMirror) {
    return {
      className: `cp-program-presentation-list-open-cell cp-program-presentation-list-open-cell-disabled${extraClass ? ` ${extraClass}` : ''}`,
      title: SYNC_MIRROR_ROW_TITLE,
      'aria-disabled': true,
    };
  }
  return {
    className: `cp-program-presentation-list-open-cell${extraClass ? ` ${extraClass}` : ''}`,
    tabIndex: 0,
    role: 'button',
    onClick: onOpen,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

function collectSlideOwnersFromItems(items: ProgramPresentationListItem[]): NonNullable<ProgramPresentationListItem['slideOwner']>[] {
  const seen = new Set<string>();
  const owners: NonNullable<ProgramPresentationListItem['slideOwner']>[] = [];
  items.forEach((it) => {
    const list = it.slideOwnerCandidates?.length
      ? it.slideOwnerCandidates
      : it.slideOwner
        ? [it.slideOwner]
        : [];
    list.forEach((owner) => {
      if (!owner?.id?.trim()) return;
      const key = programContentSlidePresenceKey(owner);
      if (seen.has(key)) return;
      seen.add(key);
      owners.push(owner);
    });
  });
  return owners;
}

function applyStatsToItems(
  items: ProgramPresentationListItem[],
  statsMap: Map<string, { pageCount: number; layoutBytes: number }>
): ProgramPresentationListItem[] {
  return items.map((it) => {
    const owners = it.slideOwnerCandidates?.length
      ? it.slideOwnerCandidates
      : it.slideOwner
        ? [it.slideOwner]
        : [];
    const ownerKeys = owners.map((o) => programContentSlidePresenceKey(o));
    const totalPageCount = sumSlidePageCountForOwners(statsMap, ownerKeys);
    const primaryStats = it.slideOwner ? statsMap.get(programContentSlidePresenceKey(it.slideOwner)) : undefined;
    const linkState = resolvePresentationRefSlideLinkState({
      hasRef: !!it.hasPresentationRef,
      slidePageCount: totalPageCount,
    });
    return {
      ...it,
      pageCount: totalPageCount > 0 ? totalPageCount : primaryStats?.pageCount,
      layoutBytes: ownerKeys.reduce((sum, key) => sum + (statsMap.get(key)?.layoutBytes ?? 0), 0) || primaryStats?.layoutBytes,
      isDraftOnly: totalPageCount <= 0 && !!it.isDraftOnly,
      linkState,
      linkStateLabel: presentationRefSlideLinkStateLabel(linkState),
    };
  });
}

export type ProgramPresentationListActionState = {
  canCopy: boolean;
  canDelete: boolean;
};

export type ProgramPresentationListModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  items: ProgramPresentationListItem[];
  onOpenItem: (annualCellKey: string) => void;
  title?: string;
  getActionState?: (annualCellKey: string) => ProgramPresentationListActionState;
  onCopy?: (annualCellKey: string) => boolean;
  onDelete?: (annualCellKey: string) => boolean;
  actionsDisabled?: boolean;
  onNotify?: (message: string) => void;
};

const ProgramPresentationListModal: React.FC<ProgramPresentationListModalProps> = ({
  open,
  onClose,
  workspaceId,
  items,
  onOpenItem,
  title = '프레젠테이션 목록',
  getActionState,
  onCopy,
  onDelete,
  actionsDisabled = false,
  onNotify,
}) => {
  const showActions = !!(getActionState && onCopy && onDelete);

  const slideOwners = React.useMemo(() => collectSlideOwnersFromItems(items), [items]);

  const ownerKeysSignature = React.useMemo(() => {
    const keys = slideOwners.map((o) => programContentSlidePresenceKey(o));
    return keys.sort().join('|');
  }, [slideOwners]);

  const lsFullHit = React.useMemo(() => {
    if (!open || !workspaceId.trim() || !slideOwners.length) return false;
    const ownerKeys = slideOwners.map((o) => programContentSlidePresenceKey(o));
    const cachedMap = readProgramPresentationListStatsCache(workspaceId, ownerKeys);
    return ownerKeys.every((key) => cachedMap.has(key));
  }, [open, slideOwners, workspaceId]);

  const statsQuery = usePresentationOwnerStatsQuery({
    workspaceId,
    owners: slideOwners,
    ownerKeysSignature,
    enabled: open && !!workspaceId.trim() && slideOwners.length > 0,
    forceRefresh: false,
  });

  const statsMap = statsQuery.data ?? new Map<string, { pageCount: number; layoutBytes: number }>();

  const rows = React.useMemo(() => {
    if (!open) return [] as ProgramPresentationListItem[];
    if (!items.length) return [] as ProgramPresentationListItem[];
    if (!slideOwners.length) return items;
    if (statsMap.size > 0 || lsFullHit) {
      return applyStatsToItems(items, statsMap);
    }
    return [] as ProgramPresentationListItem[];
  }, [items, lsFullHit, open, slideOwners.length, statsMap]);

  const loadingStats =
    open &&
    !!slideOwners.length &&
    !lsFullHit &&
    (statsQuery.isPending || statsQuery.isFetching);

  const statsError = statsQuery.isError
    ? statsQuery.error instanceof Error
      ? statsQuery.error.message
      : String(statsQuery.error)
    : null;

  const openRow = (annualCellKey: string) => {
    onOpenItem(annualCellKey);
  };

  const handleCopy = (annualCellKey: string) => {
    if (!onCopy) return;
    if (onCopy(annualCellKey)) return;
    onNotify?.('복사할 프레젠테이션이 없습니다. 먼저 프레젠테이션을 만들어 주세요.');
  };

  const handleDelete = (annualCellKey: string) => {
    if (!onDelete) return;
    if (onDelete(annualCellKey)) {
      onNotify?.('해당 학습내용의 프레젠테이션이 삭제되었습니다.');
      return;
    }
    onNotify?.('삭제할 프레젠테이션이 없습니다.');
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="cp-modal cp-program-presentation-list-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-program-presentation-list-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3 id="cp-program-presentation-list-title">{title}</h3>
        <p className="page-desc cp-program-presentation-list-intro">
          이 화면에 표시·기록된 프레젠테이션입니다. 행을 클릭하면 새 탭에서 엽니다(모달은 유지됩니다). 용량은 저장된 슬라이드
          데이터 크기 합(추정)입니다. <strong>연결</strong> 열에서 ref·슬라이드 조합을 확인할 수 있습니다.
          <span className="cp-presentation-audit-orphan-note"> 고아</span> 행은 이 화면 범위에서 끊긴 슬라이드입니다.
        </p>
        {statsError ? (
          <p className="cp-program-data-cleanup-error" role="alert">
            {statsError}
          </p>
        ) : null}
        <div
          className="cp-modal-body cp-program-presentation-list-body"
          aria-busy={loadingStats || undefined}
        >
          {loadingStats ? (
            <ModalBodyLoadingSpinner label="프레젠테이션 목록 불러오는 중" />
          ) : rows.length ? (
            <div className="cp-program-presentation-list-table-wrap">
              <table className="cp-program-presentation-list-table">
                <thead>
                  <tr>
                    <th scope="col">월일</th>
                    <th scope="col">요일</th>
                    <th scope="col">교시</th>
                    <th scope="col">편제</th>
                    <th scope="col">시간표명</th>
                    <th scope="col">단원</th>
                    <th scope="col">학습내용</th>
                    <th scope="col">연결</th>
                    <th scope="col">페이지</th>
                    <th scope="col">용량</th>
                    {showActions ? <th scope="col">작업</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isSyncMirror = !!row.isSyncMirror;
                    const isOrphan = !!row.isOrphan;
                    const openRowHandler = () => openRow(row.annualCellKey);
                    const pageLabel = row.isDraftOnly
                      ? '초안'
                      : row.pageCount != null
                        ? `${row.pageCount}`
                        : '—';
                    const sizeLabel = row.isDraftOnly
                      ? '—'
                      : row.layoutBytes != null
                        ? formatPresentationLayoutBytes(row.layoutBytes)
                        : '—';
                    const actionState = showActions
                      ? getActionState!(row.annualCellKey)
                      : { canCopy: false, canDelete: false };
                    const rowClassName = [
                      'cp-program-presentation-list-row',
                      isSyncMirror ? 'cp-program-presentation-list-row-sync-mirror' : '',
                      isOrphan ? 'cp-program-presentation-list-row-orphan' : '',
                      row.linkState === 'ref_no_slides' || row.linkState === 'slides_no_ref'
                        ? 'cp-program-presentation-list-row-link-warning'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const rowTitle = isSyncMirror
                      ? SYNC_MIRROR_ROW_TITLE
                      : isOrphan
                        ? row.orphanLinkLabel
                        : undefined;
                    const templateTitle = isSyncMirror
                      ? SYNC_MIRROR_ROW_TITLE
                      : isOrphan
                        ? row.orphanLinkLabel || row.templateLabel
                        : row.templateLabel;
                    return (
                      <tr key={row.annualCellKey} className={rowClassName} title={rowTitle}>
                        <td {...presentationListOpenCellProps(row, openRowHandler)}>{row.dateLabel || '—'}</td>
                        <td {...presentationListOpenCellProps(row, openRowHandler)}>{row.weekdayLabel || '—'}</td>
                        <td {...presentationListOpenCellProps(row, openRowHandler)}>{row.periodLabel || '—'}</td>
                        <td {...presentationListOpenCellProps(row, openRowHandler)} title={templateTitle}>
                          {row.templateLabel || '—'}
                        </td>
                        <td
                          {...presentationListOpenCellProps(row, openRowHandler)}
                          title={isSyncMirror ? SYNC_MIRROR_ROW_TITLE : isOrphan ? row.orphanLinkLabel || row.timetableText : row.timetableText}
                        >
                          {row.timetableText || '—'}
                        </td>
                        <td {...presentationListOpenCellProps(row, openRowHandler)} title={isSyncMirror ? SYNC_MIRROR_ROW_TITLE : row.unitText}>
                          {row.unitText || '—'}
                        </td>
                        <td {...presentationListOpenCellProps(row, openRowHandler)} title={isSyncMirror ? SYNC_MIRROR_ROW_TITLE : row.learningText}>
                          {row.learningText || '—'}
                        </td>
                        <td
                          {...presentationListOpenCellProps(row, openRowHandler)}
                          title={row.linkStateLabel || '—'}
                        >
                          {row.linkStateLabel || '—'}
                        </td>
                        <td {...presentationListOpenCellProps(row, openRowHandler, 'cp-program-presentation-list-col-num')}>
                          {pageLabel}
                        </td>
                        <td {...presentationListOpenCellProps(row, openRowHandler, 'cp-program-presentation-list-col-num')}>
                          {sizeLabel}
                        </td>
                        {showActions ? (
                          <td className="cp-program-presentation-list-actions-cell">
                            <ProgramPresentationListRowActions
                              annualCellKey={row.annualCellKey}
                              canCopy={actionState.canCopy && !isSyncMirror}
                              canDelete={actionState.canDelete && !isSyncMirror}
                              disabled={actionsDisabled || isSyncMirror}
                              onCopy={handleCopy}
                              onDelete={handleDelete}
                            />
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="page-desc">이 화면에 연결된 프레젠테이션이 없습니다.</p>
          )}
        </div>
        <div className="cp-modal-footer cp-program-presentation-list-footer">
          <button type="button" className="cp-btn-secondary" onClick={() => onClose()}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProgramPresentationListModal;
